import { isBrowser } from './isBrowser';

export enum SameSite {
  Lax = 'Lax',
  Strict = 'Strict',
  None = 'None',
}

export type CookieOptions = {
  /**
   * The expiry date of the cookie. Can be a Date object or a timestamp.
   */
  expiry?: number | Date;
  /**
   * Define the path where the cookie is available. Defaults to '/'
   */
  path?: string;
  /**
   * Define the domain where the cookie is available. Defaults to
   * the domain of the page where the cookie was created.
   */
  domain?: string;
  /**
   * A Boolean indicating if the cookie transmission requires a
   * secure protocol (https). Defaults to false.
   */
  secure?: boolean;
  /**
   * Asserts that a cookie must not be sent with cross-origin requests,
   * providing some protection against cross-site request forgery
   * attacks (CSRF).
   */
  sameSite?: SameSite;
};

/**
 * Sets a cookie with the specified name and value, along with optional settings.
 *
 * @param name - The name of the cookie.
 * @param value - The value of the cookie.
 * @param options - Optional settings for the cookie.
 *
 * @remarks
 * Use `setStringifiedCookie` to set a cookie with a stringified JSON value.
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
) => {
  if (!isBrowser) return;

  const serializedValue = encodeURIComponent(value);
  const parts = [`${encodeURIComponent(name)}=${serializedValue}`];
  const { expiry, path, domain, secure, sameSite } = options;

  if (expiry instanceof Date) {
    parts.push(`expires=${expiry.toUTCString()}`);
  } else if (typeof expiry === 'number') {
    parts.push(`expires=${new Date(expiry).toUTCString()}`);
  }

  if (path) parts.push(`path=${path}`);
  if (domain) parts.push(`domain=${domain}`);
  if (secure) parts.push('secure');
  if (sameSite) parts.push(`samesite=${sameSite}`);

  document.cookie = parts.join('; ');
};

/**
 * Sets a cookie with a stringified JSON value.
 *
 * @template T - The type of the value to be stringified and stored in the cookie.
 * @param name - The name of the cookie.
 * @param value - The value to be stringified and stored in the cookie.
 * @param options - Optional settings for the cookie.
 */
export const setStringifiedCookie = <T>(
  name: string,
  value: T,
  options: CookieOptions = {},
) => setCookie(name, JSON.stringify(value), options);

/**
 * Retrieves the value of a cookie by its name.
 *
 * @param name - The name of the cookie to retrieve.
 * @returns The value of the cookie, or null if the cookie is not found.
 *
 * @remarks
 * Use `getParsedCookie` to retrieve the value of a cookie as a parsed object.
 */
export const getCookie = (name: string) => {
  if (!isBrowser) return null;

  const cookieString = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${encodeURIComponent(name)}=`));

  if (!cookieString) return null;
  return decodeURIComponent(cookieString.split('=')[1]);
};

/**
 * Retrieves a cookie by its name and parses its value as JSON.
 *
 * @template T - The expected type of the parsed cookie value.
 * @param name - The name of the cookie to retrieve.
 * @returns {T | null} - The parsed cookie value, or null if the cookie does not exist or cannot be parsed.
 */
export const getParsedCookie = <T>(name: string) => {
  const cookie = getCookie(name);
  if (!cookie) return null;

  try {
    return JSON.parse(cookie) as T;
  } catch {
    return null;
  }
};

/**
 * Removes a cookie by setting its expiry date to a past date.
 *
 * @param name - The name of the cookie to remove.
 * @param options - Optional settings for the cookie, excluding the expiry date.
 */
export const removeCookie = (
  name: string,
  options: Omit<CookieOptions, 'expiry'> = {},
) => {
  setCookie(name, '', { ...options, expiry: new Date(0) });
};
